import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/utils/logger';
import { useMutation } from 'convex/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../../convex/_generated/api';

/**
 * Analytics consent state
 */
interface AnalyticsConsent {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

/**
 * Analytics session data
 */
interface AnalyticsSession {
  sessionId: string;
  startTime: number;
  lastActivity: number;
  isActive: boolean;
  visitorHash: string;
}

/**
 * Photo selection event data
 */
interface PhotoSelectionEvent {
  photoId: string;
  action: 'favorite' | 'selected' | 'removed';
  galleryId: string;
}

/**
 * Download event data
 */
interface DownloadEvent {
  photoId: string;
  downloadType?: 'full' | 'preview' | 'thumbnail';
}

// Note: PhotoViewEvent and GalleryViewEvent interfaces reserved for future use

/**
 * Analytics configuration
 */
interface AnalyticsConfig {
  sessionTimeout: number; // in milliseconds
  respectDoNotTrack: boolean;
}

const DEFAULT_CONFIG: AnalyticsConfig = {
  sessionTimeout: 30 * 60 * 1000, // 30 minutes
  respectDoNotTrack: true,
};

/**
 * Generate a visitor hash from browser fingerprint
 */
function generateVisitorHash(): string {
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
  ].join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return `visitor_${Math.abs(hash).toString(36)}`;
}

/**
 * Check if analytics tracking should be enabled based on consent and browser settings
 */
function shouldTrack(consent: AnalyticsConsent | null, respectDoNotTrack: boolean): boolean {
  // Check Do Not Track header if configured
  if (respectDoNotTrack && navigator.doNotTrack === '1') {
    return false;
  }

  // Check user consent
  if (!consent || !consent.analytics) {
    return false;
  }

  return true;
}

/**
 * Analytics hook providing tracking functions for photos and galleries
 *
 * Features:
 * - Photo view tracking with intersection observer
 * - Photo selection tracking (favorite, select, remove)
 * - Download tracking
 * - Gallery view tracking
 * - Session management with privacy compliance
 * - Consent management for GDPR/CCPA compliance
 *
 * @param config - Analytics configuration options
 */
export function useAnalytics(config: Partial<AnalyticsConfig> = {}) {
  const fullConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);
  const { user } = useAuth();

  // State management
  const [consent, setConsent] = useState<AnalyticsConsent | null>(() => {
    if (typeof window === 'undefined') return null;

    const stored = localStorage.getItem('analytics_consent');
    return stored ? JSON.parse(stored) : null;
  });

  const [session, setSession] = useState<AnalyticsSession | null>(() => {
    if (typeof window === 'undefined') return null;

    const stored = sessionStorage.getItem('analytics_session');
    return stored ? JSON.parse(stored) : null;
  });

  const [isInitialized, setIsInitialized] = useState(false);

  // Refs for tracking
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingEvents = useRef<Set<string>>(new Set());

  // Convex mutations
  const trackDownloadMutation = useMutation(api.analytics.trackDownload);
  const trackPhotoSelectionMutation = useMutation(api.analytics.trackPhotoSelection);
  const startSessionMutation = useMutation(api.analytics.startAnalyticsSession);

  /**
   * Get account ID from current user
   */
  const getAccountId = useCallback(() => {
    return user?.id || 'anonymous';
  }, [user?.id]);

  /**
   * Get device type from user agent
   */
  const getDeviceType = useCallback(() => {
    return /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop';
  }, []);

  /**
   * Initialize or update analytics session
   */
  const initializeSession = useCallback(async () => {
    if (!shouldTrack(consent, fullConfig.respectDoNotTrack)) return;

    try {
      const now = Date.now();
      const visitorHash = generateVisitorHash();
      let currentSession = session;

      // Check if we need a new session
      const needNewSession =
        !currentSession ||
        now - currentSession.lastActivity > fullConfig.sessionTimeout ||
        !currentSession.isActive;

      if (needNewSession) {
        // Start session in backend
        const result = await startSessionMutation({
          visitorHash,
          accountId: getAccountId(),
          deviceType: getDeviceType(),
        });

        if (result.success) {
          currentSession = {
            sessionId: result.sessionId,
            startTime: now,
            lastActivity: now,
            isActive: true,
            visitorHash,
          };
        } else {
          throw new Error('Failed to start session');
        }
      } else if (currentSession) {
        // Update last activity
        currentSession = {
          ...currentSession,
          lastActivity: now,
        };
      }

      if (currentSession) {
        setSession(currentSession);
        sessionStorage.setItem('analytics_session', JSON.stringify(currentSession));

        // Set up session timeout
        if (sessionTimeoutRef.current) {
          clearTimeout(sessionTimeoutRef.current);
        }

        sessionTimeoutRef.current = setTimeout(() => {
          setSession((prev) => (prev ? { ...prev, isActive: false } : null));
        }, fullConfig.sessionTimeout);

        logger.debug('Analytics session initialized', 'analytics', {
          sessionId: currentSession.sessionId,
          isNewSession: needNewSession,
        });
      }
    } catch (error) {
      logger.errorWithException('Failed to initialize analytics session', error, 'analytics');
    }
  }, [consent, session, fullConfig, startSessionMutation, getAccountId, getDeviceType]);

  /**
   * Track a photo selection/interaction event
   */
  const trackPhotoSelection = useCallback(
    async (event: PhotoSelectionEvent) => {
      if (!shouldTrack(consent, fullConfig.respectDoNotTrack) || !session?.isActive) return;

      const eventKey = `photo_${event.action}_${event.photoId}`;

      // Prevent duplicate events within short timeframe
      if (pendingEvents.current.has(eventKey)) return;
      pendingEvents.current.add(eventKey);

      try {
        await trackPhotoSelectionMutation({
          photoId: event.photoId,
          galleryId: event.galleryId,
          accountId: getAccountId(),
          customerId: user?.id,
          visitorHash: session.visitorHash,
          selectionType: event.action,
          consentGiven: consent?.analytics || false,
        });

        logger.debug('Photo selection tracked', 'analytics', {
          photoId: event.photoId,
          action: event.action,
          sessionId: session.sessionId,
        });
      } catch (error) {
        logger.errorWithException('Failed to track photo selection', error, 'analytics');
      } finally {
        // Clear the event key after a short delay to allow for rapid interactions
        setTimeout(() => {
          pendingEvents.current.delete(eventKey);
        }, 1000);
      }
    },
    [consent, session, fullConfig, trackPhotoSelectionMutation, getAccountId, user?.id]
  );

  /**
   * Track a download event
   */
  const trackDownload = useCallback(
    async (event: DownloadEvent) => {
      if (!shouldTrack(consent, fullConfig.respectDoNotTrack) || !session?.isActive) return;

      try {
        await trackDownloadMutation({
          photoId: event.photoId,
          accountId: getAccountId(),
          visitorHash: session.visitorHash,
          downloadType: event.downloadType,
          deviceType: getDeviceType(),
          consentGiven: consent?.analytics || false,
        });

        logger.debug('Download tracked', 'analytics', {
          photoId: event.photoId,
          downloadType: event.downloadType,
          sessionId: session.sessionId,
        });
      } catch (error) {
        logger.errorWithException('Failed to track download', error, 'analytics');
      }
    },
    [consent, session, fullConfig, trackDownloadMutation, getAccountId, getDeviceType]
  );

  /**
   * Update analytics consent
   */
  const updateConsent = useCallback(
    (newConsent: Partial<AnalyticsConsent>) => {
      const updatedConsent = { ...consent, ...newConsent } as AnalyticsConsent;
      setConsent(updatedConsent);
      localStorage.setItem('analytics_consent', JSON.stringify(updatedConsent));

      logger.debug('Analytics consent updated', 'analytics', updatedConsent);

      // If analytics consent is revoked, end current session
      if (!updatedConsent.analytics && session?.isActive) {
        setSession((prev) => (prev ? { ...prev, isActive: false } : null));
        if (sessionTimeoutRef.current) {
          clearTimeout(sessionTimeoutRef.current);
        }
      }

      // If analytics consent is granted, initialize session
      if (updatedConsent.analytics && !session?.isActive) {
        initializeSession();
      }
    },
    [consent, session, initializeSession]
  );

  /**
   * Get current analytics status
   */
  const getAnalyticsStatus = useCallback(
    () => ({
      isEnabled: shouldTrack(consent, fullConfig.respectDoNotTrack),
      hasConsent: consent?.analytics || false,
      session: session?.isActive ? session : null,
      isInitialized,
    }),
    [consent, session, isInitialized, fullConfig]
  );

  // Initialize session when consent is available
  useEffect(() => {
    if (consent?.analytics && !isInitialized) {
      initializeSession().then(() => setIsInitialized(true));
    }
  }, [consent?.analytics, isInitialized, initializeSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Tracking functions
    trackPhotoSelection,
    trackDownload,

    // Consent management
    updateConsent,
    consent,

    // Session management
    initializeSession,

    // Status
    ...getAnalyticsStatus(),

    // Configuration
    config: fullConfig,
  };
}
